import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import { USER_ROLE_APARTMENT_LOCATOR } from 'common/userRoles';
import {
  SCREEN_ID_APARTMENT_LOCATOR_LEADS_LIST,
  SCREEN_ID_APARTMENT_LOCATOR_LEAD,
  SCREEN_ID_APARTMENT_LOCATOR_FEED,
} from 'client/constants/screenIds';
import { DATA_TABLE_SOURCE_AGENT_LEADS } from 'common/dataTableConstants';

import {
  APARTMENT_LOCATOR_EDIT_LEAD,
  APARTMENT_LOCATOR_ARCHIVE_LEAD,
  APARTMENT_LOCATOR_UN_ARCHIVE_LEAD,
} from 'common/apartmentLocatorHelpers';
import { LEAD_STATUS_ARCHIVED, LEAD_STATUS_ASSIGNED } from 'common/leadConstants';

import getLastPageVisitedByScreenIdList from 'common/util/getLastPageVisitedByScreenIdList';

import Navbar from 'client/components/Navbar';
import SrLink from 'client/components/SrLink/SrLink';
import SrAgentLeadHeader from 'client/components/SrAgentLeadHeader';
import SrAgentLeadInformationList from 'client/components/SrAgentLeadInformationList';
import SrAgentLeadListingActivity from 'client/components/SrAgentLeadListingActivity';
import SrAgentLeadLeaseForm from 'client/components/SrAgentLeadLeaseForm';
import SrAgentLeadApartmentLocatorNotes from 'client/components/SrAgentLeadApartmentLocatorNotes';
import SrDialogActionModal, {
  SR_DIALOG_ACTION_THEME_DANGER,
} from 'client/components/SrDialogActionModal';

import ApartmentLocatorActions from 'client/actions/ApartmentLocator';
import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import { dataTable, actionDataTableLoadItems } from 'client/helpers/dataTables';

const screenIdList = [SCREEN_ID_APARTMENT_LOCATOR_FEED, SCREEN_ID_APARTMENT_LOCATOR_LEADS_LIST];

function getDataTableSettingsFromProps({ match, currentUser }) {
  return {
    viewId: `leads-id-${match.params.renterUserId}`,
    filter: {
      apartmentLocatorUserId: currentUser.id,
      renterUserId: parseInt(match.params.renterUserId, 10),
    },
    count: 1,
  };
}

@withTransitionHook
@withScrollToTop
@allowedRoles({
  roles: [USER_ROLE_APARTMENT_LOCATOR],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_APARTMENT_LOCATOR_LEAD)
@branch({
  currentUser: ['currentUser'],
  screenIdHistory: ['analytics', 'screenIdHistory'],
  pathHistory: ['analytics', 'pathHistory'],
})
@dataTable((props) => ({
  dataSource: DATA_TABLE_SOURCE_AGENT_LEADS,
  ...getDataTableSettingsFromProps(props),
}))
class AgentLeadPage extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    items: PropTypes.array.isRequired,
    screenIdHistory: PropTypes.array.isRequired,
    pathHistory: PropTypes.array.isRequired,
  };

  constructor() {
    super();
    this.state = {
      showEditLeadModal: false,
      showSubmitLeaseFormMessage: false,
    };
  }

  componentDidMount() {
    const { dispatch, location } = this.props;
    this._reloadDataTableAgentLeads();

    const queryUrl = queryString.parse(location.search);
    const activityId = queryUrl.activity_id ? parseInt(queryUrl.activity_id, 10) : null;
    if (activityId) {
      dispatch(ApartmentLocatorActions.markSeenActivity, {
        activityId: activityId,
      });
    }
  }

  _onLeadLeaseFormRef = (leadLeaseFormRef) => {
    this.leadLeaseFormRef = leadLeaseFormRef;

    if (this.props.location.search.includes('scrollTo=leaseForm')) {
      this._goToLeaseForm();
    }
  };

  _goToLeaseForm = () => {
    if (!this.leadLeaseFormRef) {
      return;
    }

    this.setState(
      {
        showSubmitLeaseFormMessage: true,
      },
      () => {
        this.leadLeaseFormRef.scrollIntoView({ behavior: 'smooth' });
      },
    );
  };

  _reloadDataTableAgentLeads = () => {
    const { dispatch } = this.props;
    return dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_AGENT_LEADS,
      ...getDataTableSettingsFromProps(this.props),
    });
  };

  _onClickEditLeadOption = (actionId) => {
    const { match, history, dispatch, items } = this.props;
    const renterUserId = parseInt(match.params.renterUserId, 10);
    const lead = items[0];

    if (actionId === APARTMENT_LOCATOR_EDIT_LEAD) {
      return history.push(`/agent/lead/${renterUserId}/edit`);
    }
    if (actionId === APARTMENT_LOCATOR_ARCHIVE_LEAD) {
      return history.push(`/agent/lead/${renterUserId}/archive`);
    }
    if (actionId === APARTMENT_LOCATOR_UN_ARCHIVE_LEAD) {
      dispatch(ApartmentLocatorActions.updateLeadStatus, {
        apartmentLocatorUserId: lead.apartmentLocatorUserId,
        renterUserId: lead.renterUserId,
        fromLeadStatus: LEAD_STATUS_ARCHIVED,
        toLeadStatus: LEAD_STATUS_ASSIGNED,
      })
        .then(() => {
          return this._reloadDataTableAgentLeads();
        })
        .then(() => history.push('/agent/leads/assigned'));
    }

    this._closeEditLeadModal();
  };

  _closeEditLeadModal = () => {
    this.setState({ showEditLeadModal: false });
  };

  render() {
    const { currentUser, items, screenIdHistory, pathHistory } = this.props;
    const { showEditLeadModal, showSubmitLeaseFormMessage } = this.state;
    const lead = items[0];

    let backLinkUrl = `/agent/leads/${lead ? lead.status : ''}`;
    const lastPageVisited = getLastPageVisitedByScreenIdList({
      screenIdHistory,
      pathHistory,
      screenIdList,
    });

    if (lastPageVisited) {
      backLinkUrl = lastPageVisited.path;
    }

    const editModalActions = [
      {
        id: APARTMENT_LOCATOR_EDIT_LEAD,
        text: 'Edit Info',
      },
    ];

    if (lead && lead.status === LEAD_STATUS_ARCHIVED) {
      editModalActions.push({
        id: APARTMENT_LOCATOR_UN_ARCHIVE_LEAD,
        text: 'Un-Archive Lead',
      });
    } else {
      editModalActions.push({
        id: APARTMENT_LOCATOR_ARCHIVE_LEAD,
        text: 'Archive Lead',
        theme: SR_DIALOG_ACTION_THEME_DANGER,
      });
    }

    return (
      <div className="page-agentDashboard contactProfile">
        <Navbar
          redirectLogoTo={getUserLandingPage({ userRoles: currentUser.roles })}
          leftButton={<SrLink to={backLinkUrl} className="link back" restoreScrollPosition />}
          rightButton={
            <div
              className="editLeadLink blue-bright"
              onClick={() => this.setState({ showEditLeadModal: true })}
            >
              Edit
            </div>
          }
        />

        {lead && (
          <div>
            <SrAgentLeadHeader
              lead={lead}
              reloadTable={this._reloadDataTableAgentLeads}
              goToLeaseForm={this._goToLeaseForm}
            />

            <div className="mainContentContainer">
              <SrAgentLeadListingActivity lead={lead} />
              <SrAgentLeadInformationList lead={lead} />
              <SrAgentLeadApartmentLocatorNotes
                lead={lead}
                reloadTable={this._reloadDataTableAgentLeads}
              />
              <div ref={this._onLeadLeaseFormRef}>
                <SrAgentLeadLeaseForm
                  lead={lead}
                  reloadTable={this._reloadDataTableAgentLeads}
                  showSubmitLeaseFormMessage={showSubmitLeaseFormMessage}
                />
              </div>
            </div>
            <SrDialogActionModal
              isOpen={showEditLeadModal}
              title="Edit Lead"
              onClickAction={this._onClickEditLeadOption}
              actions={editModalActions}
              onClose={this._closeEditLeadModal}
            />
          </div>
        )}
      </div>
    );
  }
}

AgentLeadPage.displayName = 'AgentLeadPage';

export default AgentLeadPage;
